import type { ChangeEvent } from 'ant-design-vue/es/_util/EventInterface'
import type { Dayjs } from 'dayjs'

import { isObject } from '@vben/utils'

import { isFinite, isNil } from 'lodash-es'

import { dayjs } from './dayjs'

type DateRangeValue = Date | Dayjs | number | string

export function formatNumber(e: ChangeEvent | string) {
  const isEvent = isObject(e)
  let value = isEvent ? (e.target?.value ?? '') : e

  // 处理输入值
  value = value
    .replaceAll(/[^\d.]/g, '') // 移除非数字和小数点
    .replaceAll(/(\..*)\./g, '$1') // 只保留第一个小数点

  // 处理前导零的情况
  if (
    value.length > 1 && // 如果是以0开头但后面不是小数点，则移除前导零
    value.startsWith('0') &&
    value[1] !== '.'
  ) {
    value = value.slice(1)
  }

  // 检查是否包含小数点
  const hasDecimalPoint = value.includes('.')
  const [intPart, decimalPart = ''] = value.split('.')

  // 如果小数部分超过两位，则格式化为两位
  if (decimalPart.length > 2) {
    const formatted = Number(`${intPart}.${decimalPart}`).toFixed(2)
    if (isEvent) {
      e.target.value = formatted
    }
    return formatted
  }

  // 组合结果 - 如果原始输入包含小数点，则保留小数点
  const result = intPart + (hasDecimalPoint ? `.${decimalPart}` : '')

  // 验证最终值必须大于0
  const numValue = Number(result)
  if (result && numValue <= 0) {
    // 如果值小于等于0，则清空输入框
    if (isEvent) {
      e.target.value = ''
    }
    return ''
  }

  if (isEvent) {
    e.target.value = result
  }
  return result
}

export function formatTrackingNo(no: string) {
  return `https://t.17track.net/en#nums=${no}`
}

export function formatDateRange(
  date: DateRangeValue[] | null | undefined,
): [] | [Dayjs, Dayjs] | undefined {
  if (!date) return undefined
  if (date.length === 0) return []

  const targetDate = Array.isArray(date) ? [date[0], date[1]] : [date, date]
  return [
    dayjs(targetDate[0]).startOf('day'),
    dayjs(targetDate[1]).endOf('day'),
  ] as [Dayjs, Dayjs]
}

// Preserve selected time (no start/end of day) and output ISO with timezone.
export function formatDateRangeToIso(
  dateRange: Array<Date | number | string> | undefined,
) {
  if (!Array.isArray(dateRange)) return { start: '', end: '' }
  return {
    start: dateRange.length > 0 ? dayjs(dateRange[0]).toISOString() : '',
    end: dateRange.length > 1 ? dayjs(dateRange[1]).toISOString() : '',
  }
}

export function formatDisplayText(
  value: null | number | string | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  return String(value)
}

/**
 * 格式化数字，添加千分位分隔符
 * @param num 数字
 * @param maximumFractionDigits 最大小数位数，默认 2
 * @param minimumFractionDigits 最小小数位数，默认 0
 * @returns 格式化后的字符串
 */
export function formatNumberWithCommas(
  num?: null | number | string,
  maximumFractionDigits: number = 2,
  minimumFractionDigits: number = 0,
): string {
  return isNil(num) || num === ''
    ? '-'
    : Number(num).toLocaleString('en-US', {
        maximumFractionDigits,
        minimumFractionDigits,
      })
}

export function formatNumberWithSign(
  num?: null | number | string,
  maximumFractionDigits: number = 2,
  minimumFractionDigits: number = 0,
): string {
  if (isNil(num) || num === '') return '-'

  const value = Number(num)
  if (Number.isNaN(value)) return '-'

  const formattedValue = formatNumberWithCommas(
    value,
    maximumFractionDigits,
    minimumFractionDigits,
  )

  return value > 0 ? `+${formattedValue}` : formattedValue
}

export function formatPercentText(
  value?: null | number | string,
  maximumFractionDigits: number = 2,
  minimumFractionDigits: number = 0,
): string {
  if (isNil(value) || value === '') return '-'

  const numericValue = Number(value)
  if (!isFinite(numericValue)) return '-'

  return `${formatNumberWithCommas(
    numericValue,
    maximumFractionDigits,
    minimumFractionDigits,
  )}%`
}
